/**
 * Construction du « rapport d'erreur » copiable (§3.3).
 *
 * Ce texte est destiné à être collé dans un ticket ou une conversation avec le
 * service informatique : il doit se suffire à lui-même. Les horodatages sont
 * donc laissés au format ISO 8601 — non ambigus, indépendants du fuseau du
 * poste qui a produit le rapport, et directement recherchables dans les logs
 * serveur.
 */

import { STATE_LABELS, type DeploymentState } from '@portail/shared';

import { tailLines, type LogEntry } from './logs';

/** Nombre de lignes de log jointes au rapport (§3.3). */
export const ERROR_REPORT_LOG_LINES = 100;

/** Données nécessaires à la rédaction du rapport. */
export interface ErrorReportInput {
  deploymentId: string;
  slug: string;
  domain: string;
  url: string | null;
  state: DeploymentState;
  failedStep: DeploymentState | null;
  failureReason: string | null;
  createdAt: string | null;
  finishedAt: string | null;
  logs: readonly LogEntry[];
  /** Instant de rédaction, injectable pour les tests. */
  generatedAt?: string;
}

/** Aligne les libellés de la section d'en-tête sur une même colonne. */
function line(label: string, value: string): string {
  return `${label.padEnd(20, ' ')}: ${value}`;
}

/**
 * Produit le rapport d'erreur complet.
 * Le résultat est un texte brut, prêt pour le presse-papiers.
 */
export function buildErrorReport(input: ErrorReportInput): string {
  const {
    deploymentId,
    slug,
    domain,
    url,
    state,
    failedStep,
    failureReason,
    createdAt,
    finishedAt,
    logs,
  } = input;

  const generatedAt = input.generatedAt ?? new Date().toISOString();

  const header = [
    "Rapport d'erreur — portail de déploiement",
    '='.repeat(42),
    line('Identifiant du job', deploymentId),
    line('Application', `${slug}.${domain}`),
    line('URL prévue', url ?? 'non attribuée'),
    line('État', `${state} (${STATE_LABELS[state]})`),
    line(
      'Étape en échec',
      failedStep ? `${failedStep} (${STATE_LABELS[failedStep]})` : 'non déterminée',
    ),
    line('Motif', failureReason ?? 'non communiqué'),
    line('Créé le', createdAt ?? 'inconnu'),
    line('Terminé le', finishedAt ?? 'en cours'),
    line('Rapport généré le', generatedAt),
  ];

  const tail = tailLines(logs, ERROR_REPORT_LOG_LINES);
  const logHeader =
    logs.length > tail.length
      ? `--- ${tail.length} dernières lignes de log (sur ${logs.length} conservées) ---`
      : `--- ${tail.length} ligne${tail.length > 1 ? 's' : ''} de log ---`;

  const body =
    tail.length === 0
      ? ['Aucune ligne de log disponible.']
      : tail.map((entry) => `[${entry.at}] [${entry.step}] [${entry.level}] ${entry.line}`);

  return [...header, '', logHeader, ...body, ''].join('\n');
}
